"use client";

import type { HeadingLevel } from "@components/core/Heading";
import type { CustomerStoriesBlock } from "@mw/shared/payload-types";
import type * as React from "react";

import { Block } from "@/app/_blocks/Block";
import { filterEntitiesArray, isEntity } from "@/app/_utilities/payload";
import { getImagePlaceholder } from "@/app/_utilities/placeholder";
import { CardCommunityAsset } from "@components/cards/CardCommunityAsset";
import SliderGeneric from "@components/composers/SliderGenericWrapper";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/core/Tabs";
import { HeadingSection } from "@components/headings/HeadingSection";
import { ImageSizes } from "@ui/lib/image";
import { simpleSlug } from "@ui/lib/text";
import { cn } from "@ui/lib/utils";
import { useEffect, useState } from "react";

export interface BlockCustomersStoriesProps extends Pick<CustomerStoriesBlock, "categories" | "title"> {
  className?: string;
  headingLevel?: HeadingLevel;
  inverse?: boolean;
}

const BlockCustomersStories = ({
  title,
  inverse = false,
  categories,
  className,
  headingLevel = 2,
}: BlockCustomersStoriesProps) => {
  const _categories = (categories || [])
    .filter(category => category?.title && category?.customerStories?.length)
    .map((category, index) => ({
      title: category.title,
      id: simpleSlug(category.title) || index.toString(),
      items: filterEntitiesArray(category.customerStories).map((item, index) => {
        // const category = filterEntitiesArray(item.categories)[0] || null;
        const opts = isEntity(item.customer)
          ? { name: item.customer.name, avatar: { resource: item.customer.image } }
          : {};
        const _data = {
          ...opts,
          // label: category?.title,
          href: `/blog/${item.slug}`,
          title: item.pageTitle || item.title,
          img: {
            resource: item.cardCover?.image || item.cover,
            sizes: ImageSizes.FULL_HALF_ON_LG,
            ...getImagePlaceholder({ ratio: "1x1" }),
          },
        } satisfies React.ComponentProps<typeof CardCommunityAsset>;

        return <CardCommunityAsset key={index} ratio='1x1' {..._data} />;
      }),
    }))
    .filter(category => category.items.length > 0);

  const [value, setValue] = useState(_categories[0]?.id);

  useEffect(() => {
    const trackHash = () => {
      const hash = window.location.hash;
      if (hash) {
        const id = hash.replace("#", "");
        const valid = _categories.find(category => category.id === id);
        if (valid) {
          setValue(id);
        }
      }
    };

    trackHash();

    window.addEventListener("hashchange", trackHash);
    window.addEventListener("popstate", trackHash);

    return () => {
      window.removeEventListener("hashchange", trackHash);
      window.removeEventListener("popstate", trackHash);
    };
  }, [_categories]);

  const onValueChange = (value: string) => {
    window.history.replaceState(null, "", `#${value}`);
    setValue(value);
  };

  return (
    <Block className={cn(inverse ? "text-inverse" : "", className)} name='customers-stories'>
      {title && (
        <HeadingSection className='container' level={headingLevel} variant='large'>
          {title}
        </HeadingSection>
      )}
      {_categories.length > 0 && (
        <Tabs className='container mt-40 lg:mt-60' defaultValue={value} onValueChange={onValueChange} value={value}>
          <TabsList className='breakout full-bleed-scroller scrollbar-none md:breakout-reset md:flex-wrap md:before:hidden md:after:hidden'>
            {_categories.map((category, index) => (
              <TabsTrigger id={category.id} inverse={inverse} key={index} value={category.id}>
                {category.title}
              </TabsTrigger>
            ))}
          </TabsList>
          {_categories.map((category, index) => (
            <TabsContent className='container mt-28 lg:mt-40' key={index} value={category.id}>
              <SliderGeneric
                className=''
                inContainer={false}
                items={category.items}
                itemsPerViewportAtLg={3}
                onDark={inverse}
              />
            </TabsContent>
          ))}
        </Tabs>
      )}
    </Block>
  );
};

BlockCustomersStories.displayName = "BlockCustomersStories";

export { BlockCustomersStories };
